import {
  Permissions,
  Roles,
  isKnownRole,
  type AppPermission,
  type AuthorizationRequirement,
  type RolePermissionPolicy,
} from "../types/permissions";

/**
 * Explicit UX grants for today's implemented integrations; unknown server roles grant nothing.
 *
 * The school-wide absence report permission is granted explicitly here; the admin portal also
 * exposes recordings under its Admin role guard. Note that
 * `Admin` deliberately does NOT inherit Teacher's classroom capabilities: being the admin is
 * a different job, and silently granting teaching or moderation powers would widen the product
 * beyond what the backend authorizes.
 */
export const DEFAULT_ROLE_PERMISSIONS: RolePermissionPolicy = {
  [Roles.Admin]: [Permissions.AbsencesRead],
  [Roles.Teacher]: [
    Permissions.ClassroomsRead,
    Permissions.StudentsRead,
    Permissions.SessionsJoin,
    Permissions.SessionsModerate,
    Permissions.PresentationsInsert,
  ],
  [Roles.Student]: [Permissions.ClassroomsRead, Permissions.SessionsJoin],
};

/**
 * Collects all granted permissions from a set of roles based on the active policy.
 *
 * @param roles - User's active roles in the current tenant.
 * @param policy - Policy mapping roles to permissions.
 */
export function resolvePermissionsForRoles(
  roles: readonly string[],
  policy: RolePermissionPolicy = DEFAULT_ROLE_PERMISSIONS,
): AppPermission[] {
  const granted = new Set<AppPermission>();

  for (const role of roles) {
    // Narrow untrusted strings before indexing; inherited object properties are never roles.
    if (!isKnownRole(role)) continue;
    const roleRules = policy[role];
    if (roleRules) {
      for (const rule of roleRules) {
        granted.add(rule);
      }
    }
  }

  return Array.from(granted);
}

/**
 * Checks if a specific permission is granted by a list of permissions.
 *
 * @param grantedPermissions - Explicit capabilities possessed by the user.
 * @param targetPermission - The permission being checked.
 */
export function hasPermission(
  grantedPermissions: readonly AppPermission[],
  targetPermission: AppPermission,
): boolean {
  return grantedPermissions.includes(targetPermission);
}

/**
 * Pure authorization evaluator.
 * Determines whether the user's roles satisfy the provided authorization requirement.
 *
 * @param userRoles - Roles granted to the user within the active school tenant.
 * @param requirement - Target permission and/or role criteria.
 * @param policy - Configurable role-permission dictionary (defaults to DEFAULT_ROLE_PERMISSIONS).
 * @returns True if all specified requirements are satisfied, false otherwise.
 */
export function checkAuthorization(
  userRoles: readonly string[],
  requirement: AuthorizationRequirement,
  policy: RolePermissionPolicy = DEFAULT_ROLE_PERMISSIONS,
): boolean {
  // 1. Evaluate role constraints if present
  if (
    requirement.requiredRole &&
    !userRoles.includes(requirement.requiredRole)
  ) {
    return false;
  }

  if (
    requirement.allowedRoles &&
    requirement.allowedRoles.length > 0 &&
    !requirement.allowedRoles.some((role) => userRoles.includes(role))
  ) {
    return false;
  }

  // If no permission criteria were specified, role validation was sufficient
  const hasPermissionRequirement =
    Boolean(requirement.requiredPermission) ||
    Boolean(
      requirement.requiredPermissions &&
      requirement.requiredPermissions.length > 0,
    ) ||
    Boolean(requirement.anyPermission && requirement.anyPermission.length > 0);

  if (!hasPermissionRequirement) {
    return true;
  }

  // 2. Resolve granted permissions for the active roles
  const granted = resolvePermissionsForRoles(userRoles, policy);

  // 3. Evaluate single required permission
  if (
    requirement.requiredPermission &&
    !hasPermission(granted, requirement.requiredPermission)
  ) {
    return false;
  }

  // 4. Evaluate multiple required permissions (AND condition)
  if (
    requirement.requiredPermissions &&
    requirement.requiredPermissions.length > 0
  ) {
    const allSatisfied = requirement.requiredPermissions.every((perm) =>
      hasPermission(granted, perm),
    );
    if (!allSatisfied) return false;
  }

  // 5. Evaluate one-of permissions (OR condition)
  if (requirement.anyPermission && requirement.anyPermission.length > 0) {
    const atLeastOneSatisfied = requirement.anyPermission.some((perm) =>
      hasPermission(granted, perm),
    );
    if (!atLeastOneSatisfied) return false;
  }

  return true;
}
